import { ImportDialog, type ImportField } from '@/components/import/ImportDialog';
import { buildTemplate, TEMPLATE_RECOUVREMENT_ENTREPRISES } from '@/lib/import-templates';
import {
  useImportRecouvrement,
  type ImportFactureRow,
} from '@/hooks/queries/useRecouvrementEntreprises';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  entrepriseId: string;
  activiteId: string | null;
}

const FIELDS: ImportField[] = [
  { key: 'annee', label: 'Année', required: true },
  { key: 'reference_facture', label: 'Réf facture', required: true },
  { key: 'formation', label: 'Formation' },
  { key: 'date_formation', label: 'Date' },
  { key: 'lieu', label: 'Lieu' },
  { key: 'entreprise', label: 'Entreprise', required: true },
  { key: 'participants', label: 'Participants' },
  { key: 'montant_ht', label: 'HT (DA)' },
  { key: 'montant_tva', label: 'TVA (DA)' },
  { key: 'montant_ttc', label: 'TTC (DA)', required: true },
  { key: 'statut', label: 'Statut' },
  { key: 'date_reglement', label: 'Date règlement' },
  { key: 'mode_reglement', label: 'Mode' },
  { key: 'reference_virement', label: 'Référence virement' },
  { key: 'commerciale', label: 'Commerciale' },
  { key: 'observation', label: 'Observation' },
];

export function ImportRecouvrementEntreprisesDialog({
  open,
  onOpenChange,
  entrepriseId,
  activiteId,
}: Props): JSX.Element {
  const importMut = useImportRecouvrement();

  return (
    <ImportDialog<ImportFactureRow>
      open={open}
      onOpenChange={onOpenChange}
      title="Importer le recouvrement entreprises (XLSX)"
      fields={FIELDS}
      generateTemplate={() => buildTemplate(TEMPLATE_RECOUVREMENT_ENTREPRISES)}
      templateFilename={TEMPLATE_RECOUVREMENT_ENTREPRISES.filename}
      requiredHeaders={TEMPLATE_RECOUVREMENT_ENTREPRISES.requiredHeaders}
      onImport={async (rows) => {
        if (!entrepriseId) {
          return { success: 0, errors: ['Entreprise non sélectionnée'] };
        }
        const result = await importMut.mutateAsync({
          entrepriseId,
          activiteId,
          rows,
        });
        const summary: string[] = [];
        if (result.clientsCrees > 0) summary.push(`${result.clientsCrees} client(s) B2B créé(s)`);
        if (result.formationsCreees > 0)
          summary.push(`${result.formationsCreees} formation(s) créée(s)`);
        if (result.commerciauxNonTrouves.length > 0)
          summary.push(
            `${result.commerciauxNonTrouves.length} commercial(es) non trouvé(s) : ${result.commerciauxNonTrouves.slice(0, 3).join(', ')}${result.commerciauxNonTrouves.length > 3 ? '…' : ''}`,
          );
        if (summary.length > 0) result.errors.unshift(`ℹ ${summary.join(' · ')}`);
        return { success: result.facturesCrees, errors: result.errors };
      }}
    />
  );
}
